/**
 * PageSkeleton — shown by Suspense while a lazy page chunk is downloading.
 *
 * Renders the full app shell (bottom nav + desktop sidebar) so the UI never
 * goes completely blank. The content area shows shimmer cards that roughly
 * match the layout of most pages, giving the user visual continuity.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { NAV_ITEMS } from "@/config/navigation";

// ─── Reusable shimmer primitives ────────────────────────────────────────────
const Shimmer = ({ className }: { className?: string }) => (
  <div className={`animate-pulse rounded-xl bg-muted/60 ${className ?? ""}`} />
);

// ─── Bottom nav replica (functional — can be tapped during loading) ─────────
const SkeletonBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const p = location.pathname;

  const isActiveItem = (key: string): boolean => {
    switch (key) {
      case "home":       return p === "/" || p === "/home";
      case "recipes":    return p.startsWith("/recipes");
      case "glucophoto": return p.startsWith("/glucophoto");
      case "more":       return p.startsWith("/more") || p.startsWith("/settings") || p.startsWith("/shopping-list");
      default:           return false;
    }
  };

  const tabs = NAV_ITEMS.map((item) => ({
    ...item,
    active: isActiveItem(item.key),
    path: item.key === "home" ? "/home" : item.path,
  }));

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 safe-bottom md:hidden">
      <div className="max-w-[520px] mx-auto">
        <div
          className="grid grid-cols-4 px-2"
          style={{
            backgroundColor: "#E8DCC8",
            borderTop: "0.5px solid #E0D4C0",
            paddingTop: "12px",
            paddingBottom: "12px",
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const activeColor = tab.accentColor;
            const iconColor = tab.active ? activeColor : "#8C7355";
            return (
              <button
                key={tab.key}
                onClick={() => navigate(tab.path)}
                className="flex flex-col items-center gap-1 transition-all duration-200"
              >
                <Icon size={22} strokeWidth={tab.active ? 2.2 : 1.7} style={{ color: iconColor }} />
                <span className="text-[10px] leading-tight" style={{ color: iconColor, fontWeight: tab.active ? 500 : 400 }}>
                  &nbsp;
                </span>
                <div className="w-1 h-1 rounded-full" style={{ backgroundColor: tab.active ? activeColor : "transparent" }} />
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

// ─── Desktop sidebar replica (matches DesktopSidebar.tsx's layout —
// Glucofy text where the logo goes, 4 nav rows, Settings/Sign out footer —
// all as shimmer placeholders since real icons/labels/the logo image
// aren't worth loading just for a few hundred ms of Suspense fallback) ──
const SIDEBAR_SKELETON_ITEMS = ["home", "recipes", "glucophoto", "more"];

const SkeletonSidebar = () => (
  <aside
    className="hidden md:flex flex-col w-[230px] shrink-0 sticky top-0 h-screen py-8 px-4 gap-1"
    style={{ backgroundColor: "#FDFAF5", borderRight: "0.5px solid #E0D4C0" }}
  >
    <div className="px-2 mb-6">
      <p className="text-lg font-extrabold tracking-tight" style={{ color: "#1E2820" }}>Glucofy</p>
    </div>

    {SIDEBAR_SKELETON_ITEMS.map((key) => (
      <div key={key} className="flex items-center gap-3 px-3 py-3">
        <Shimmer className="h-[18px] w-[18px] rounded-md shrink-0" />
        <Shimmer className="h-3.5 w-24 rounded-md" />
      </div>
    ))}

    <div className="mt-auto flex flex-col gap-2 px-1">
      <Shimmer className="h-11 w-full rounded-xl" />
      <Shimmer className="h-11 w-full rounded-xl" />
    </div>
  </aside>
);

// ─── Main export ─────────────────────────────────────────────────────────────
const PageSkeleton = () => (
  <div className="min-h-screen bg-background flex flex-col md:flex-row">
    <SkeletonSidebar />

    <div className="flex-1 flex flex-col min-w-0">
      <div className="w-full max-w-[520px] mx-auto md:max-w-none md:px-8 lg:px-12 relative flex flex-col min-h-screen">
        <main className="flex-1 pb-20 md:pb-8 w-full md:max-w-4xl md:mx-auto lg:px-16 px-4 pt-14">

          {/* Page title area */}
          <Shimmer className="h-7 w-36 mb-2" />
          <Shimmer className="h-4 w-48 mb-6" />

          {/* Search bar */}
          <Shimmer className="h-12 w-full rounded-2xl mb-4" />

          {/* Cards grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden bg-muted/60 animate-pulse" style={{ aspectRatio: "3/4" }} />
            ))}
          </div>
        </main>

        <SkeletonBottomNav />
      </div>
    </div>
  </div>
);

export default PageSkeleton;
